import React, { useState, useRef, useEffect } from "react";
import "../../styles/interactionDetailsPage.css";
import { LevelAccessType } from "../../utils/types";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  CircularProgress
} from "@mui/material";
import GlobalSearchService from "../../services/integrations/GlobalSearchService";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { formatClientDomain, formatDateLabel, isSameCalendarDay } from "../../utils/utils";
import Ask7gArrowIcon from "../../assets/common/ask7g-arrow-icon.svg";
import Ask7G from "../../assets/client/magic-icon.svg";

interface InteractionAsk7GProps {
  suggestions: string[];
  interactionId?: string;
  interactionData?: any
  variant?: "interactionPage" | "clientLevel";
  clientDoamin?: string;
  orgId?: string;
  subOrgId?: string;
  levelAccessType?: LevelAccessType;
}

const InteractionAsk7G: React.FC<InteractionAsk7GProps> = ({ interactionId,
  interactionData,
  suggestions,
  variant = "interactionPage",
  clientDoamin,
  orgId,
  subOrgId,
  levelAccessType,
}) => {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<
    {
      sender: "user" | "bot";
      text: string;
      createdAt: number;
      isHistory?: boolean;
    }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const latestQuestionRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const { showSnackbar } = useDisplaySnackbar();
  const [query, setQuery] = useState(""); // triggers API call
  const interactionTitle = interactionData?.title;
  const inputRef = useRef<HTMLInputElement>(null);
  const loadingText = "Thinking...";
  const placeHolderText = variant === "clientLevel" ? formatClientDomain(clientDoamin || "") : interactionTitle;
  const isFirstQuestion = messages.length === 1 && messages[0]?.sender === "user";

  const askParams =
    variant === "clientLevel"
      ? {
        query: query,
        orgId: orgId,
        subOrgId: subOrgId,
        ...(levelAccessType && { levelAccessType }),
      }
      : {
        interactionId: interactionId,
        query: query,
      };
  const extraClass = variant === "clientLevel" ? "ask7g-account" : "";
  const {
    data,
    isSuccess,
    isLoading,
    isFetching,
    isError,
    error,
  } = GlobalSearchService.useGetAsk7GInteractionQuery(
    askParams,
    !!query // enabled only when a question is set
  );

  const isHistoryEnabled =
    variant === "clientLevel"
      ? !!(orgId && subOrgId)
      : !!interactionId;

  const historyParams =
    variant === "clientLevel"
      ? {
        levelType: "client",
        levelId: orgId,
        subLevelId: subOrgId,
      }
      : {
        levelType: "interaction",
        interactionId: interactionId,
      };

  const {
    isFetching: isAsk7gHistoryFetching,
    isLoading: isAsk7gHistoryLoading,
    isSuccess: isAsk7gHistorySuccess,
    isError: isAsk7gHistoryError,
    error: ask7gHistoryError,
    data: ask7gHistoryData,
  } = GlobalSearchService.useGetAsk7GHistory(historyParams, isHistoryEnabled);

  const currentHistoryKey =
    variant === "clientLevel"
      ? `${orgId}_${subOrgId}`
      : (interactionId || "");

  const historyLoadedRef = useRef<string | null>(null);

  useEffect(() => {
    if (historyLoadedRef.current !== currentHistoryKey) {
      historyLoadedRef.current = null;
    }
  }, [currentHistoryKey]);

  const renderDateSeparator = (label: string) => (
    <Box className="ask7g-date-separator">
      <Box className="ask7g-date-separator-line" />
      <Typography className="ask7g-date-separator-text">
        {label}
      </Typography>
      <Box className="ask7g-date-separator-line" />
    </Box>
  );

  useEffect(() => {
    if (isAsk7gHistorySuccess && ask7gHistoryData) {
      if (historyLoadedRef.current !== currentHistoryKey) {
        const historyList = (
          Array.isArray(ask7gHistoryData)
            ? ask7gHistoryData
            : ask7gHistoryData?.data || []
        ).sort(
          (a: any, b: any) =>
            new Date(a.createdAt).getTime() -
            new Date(b.createdAt).getTime()
        );

        const formattedMessages: { sender: "user" | "bot"; text: string; createdAt: number; isHistory?: boolean }[] = [];
        historyList.forEach((item: any) => {
        if (item.question) {
          formattedMessages.push({
            sender: "user",
            text: item.question,
            createdAt: item.createdAt,
            isHistory: true,
          });
        }

        if (item.answer) {
          formattedMessages.push({
            sender: "bot",
            text: item.answer,
            createdAt: item.createdAt,
            isHistory: true,
          });
        }
      });
        setMessages(formattedMessages);
        historyLoadedRef.current = currentHistoryKey;
      }
    } else if (isAsk7gHistoryError && ask7gHistoryError) {
      showSnackbar("error", ask7gHistoryError?.message || "Failed to load history.");
    }
  }, [
    isAsk7gHistorySuccess,
    isAsk7gHistoryError,
    ask7gHistoryData,
    ask7gHistoryError,
    currentHistoryKey,
    isAsk7gHistoryFetching,
  ]);

  useEffect(() => {
    setLoading(isLoading || isFetching)
    if (isSuccess) {
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: data?.data?.answer ?? "No response found.", createdAt: Date.now(), },
      ]);
      setQuery("");
    } else if (isError) {
      showSnackbar("error", error?.message)
      setQuery("");
    }
  }, [data, isLoading, isFetching, isError, error, showSnackbar]);


  useEffect(() => {
    // Focus when component mounts or when loading completes
    if (!loading) {
      inputRef.current?.focus();  
    }
  }, [loading]);
  const handleAsk = () => {
    if (!input.trim() || loading) return;
    const question = input.trim();

    // Add user message
    isAtBottomRef.current = true;
    setMessages((prev) => [...prev, { sender: "user", text: question, createdAt: Date.now(),}]);

    // Trigger API call
    setQuery(question);
    setInput("");
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleAsk();
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    isAtBottomRef.current = target.scrollHeight - target.scrollTop - target.clientHeight < 80;
  };

  useEffect(() => {
    if (!messageContainerRef.current) return;
    const lastMsg = messages[messages.length - 1];

    if (lastMsg?.sender === "user") {
      isAtBottomRef.current = true;
      messageContainerRef.current.scrollTo({
        top: messageContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    } else if (lastMsg?.isHistory) {
      isAtBottomRef.current = true;
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    } else if (lastMsg?.sender === "bot" && !lastMsg.isHistory) {
      if (isAtBottomRef.current && latestQuestionRef.current) {
        messageContainerRef.current.scrollTo({
          top: Math.max(0, latestQuestionRef.current.offsetTop - 8),
          behavior: "smooth",
        });
      }
    }
  }, [messages]);
  return (
    <Box className={`interaction-ask7g-container ${extraClass}`}>
      {isAsk7gHistoryLoading && isHistoryEnabled ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
            width: "100%",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#7F018B" }} />
        </Box>
      ) : (
        <>
          {/* Show greeting and suggestions before any message */}
          {messages.length === 0 && (
            <Box className={`interaction-ask7g-welcome ${extraClass}`}>
              <Box className="ask7g-expanded-header">
                <img src={Ask7G} className="img-layout" alt="Ask 7G" style={{ width: 32, height: 32 }} />
                <Typography className="ask7G-text ask7g-expanded-title">Ask 7G</Typography>
              </Box>

              <Box className="interaction-ask7g-suggestions">
                {(suggestions || []).map((q, i) => (
                  <Box
                    key={i}
                    className={`interaction-ask7g-suggestion ${extraClass}`}
                    onClick={() => {
                      setInput(q);
                      setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                  >
                    <h1 className="suggestion-text">
                      {q}
                    </h1>
                  </Box>
                ))}
              </Box>

              <Box className={`interaction-ask7g-input-top-container ${extraClass}`}>
                <TextField
                  inputRef={inputRef}
                  placeholder={`Ask me anything about ${placeHolderText}`}
                  fullWidth
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  className={`interaction-ask7g-input-textfield ${extraClass}`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end" color="primary" onClick={handleAsk} disabled={loading}>
                          <img
                            src={Ask7gArrowIcon}
                            alt="send"
                            className={`ask7g-send-icon ${input.length > 0 ? "active" : ""}`}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      "& fieldset": { border: "none" }, // remove MUI default border
                    },
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Existing chat view */}
          {messages.length > 0 && (
            <>
              <Box className={`ask7g-chat-wrapper ${isFirstQuestion ? "first-question" : ""}`}>
                <Box ref={messageContainerRef} onScroll={handleScroll} className={`interaction-ask7g-messages ${extraClass}`}>
                  {messages.map((msg, index) => {
                    const prev = messages[index - 1];
                    const showDate =
                      !!msg.createdAt &&
                      (
                        index === 0 ||
                        !prev ||
                        !isSameCalendarDay(prev.createdAt, msg.createdAt)
                      );

                    const showHistoryBoundaryDate =
                      !!msg.createdAt &&
                      prev?.isHistory &&
                      !msg.isHistory &&
                      !isSameCalendarDay(prev.createdAt, msg.createdAt);

                    const shouldRenderDate =
                      showDate || showHistoryBoundaryDate;

                    const isLatestQuestion =
                      msg.sender === "user" &&
                      !msg.isHistory &&
                      index >= messages.length - 2;

                    return (
                      <React.Fragment key={index}>
                        {shouldRenderDate &&
                          renderDateSeparator(
                            formatDateLabel(msg.createdAt)
                          )}

                        <Box
                          ref={isLatestQuestion ? latestQuestionRef : undefined}
                          className={`interaction-ask7g-message ${
                            msg.sender === "user" ? "from-user" : "from-bot"
                          }`}
                        >
                          {msg.sender !== "user" && (
                            <Box className="interaction-ask7g-icon-box">
                              <img
                                src={Ask7G}
                                alt="Ask7G"
                                className="interaction-ask7g-progress-icon"
                              />
                            </Box>
                          )}

                          <Box
                            className={`interaction-ask7g-bubble ${
                              msg.sender === "user"
                                ? "question-bubble"
                                : "response-bubble"
                            }`}
                          >
                            <Typography
                              variant="body2"
                              className="interaction-ask7g-text"
                            >
                              {msg.text}
                            </Typography>
                          </Box>
                        </Box>
                      </React.Fragment>
                    );
                  })}
                  {loading && (
                    <Box className="interaction-ask7g-message from-bot">
                      <Box className="interaction-ask7g-loading-container inline">
                        <Box className="interaction-ask7g-progress-wrapper">
                          {/* Gradient definition */}
                          <svg width="0" height="0">
                            <defs>
                              <linearGradient
                                id="ask7gGradient"
                                x1="2.52"
                                y1="32.34"
                                x2="33.6"
                                y2="8.4"
                                gradientUnits="userSpaceOnUse"
                              >
                                <stop offset="0.216892" stopColor="#35013A" stopOpacity="0.06" />
                                <stop offset="0.995265" stopColor="#7F018B" />
                              </linearGradient>
                            </defs>
                          </svg>

                          {/* Circular progress using the gradient */}
                          <CircularProgress
                            size={42}
                            thickness={3}
                            sx={{
                              color: "transparent",
                              position: "relative",
                              "& .MuiCircularProgress-circle": {
                                stroke: "url(#ask7gGradient)",
                              },
                            }}
                          />

                          {/* Center icon */}
                          <img
                            src={Ask7G}
                            alt="Ask7G"
                            className="interaction-ask7g-progress-icon"
                          />
                        </Box>

                        <Typography variant="body2" className="interaction-ask7g-loading-text">
                          {loadingText}
                        </Typography>
                      </Box>
                    </Box>

                  )}

                </Box>

                <Box className={`interaction-ask7g-input-container ${extraClass}`}>
                  <TextField
                    inputRef={inputRef}
                    placeholder={`Ask me anything about ${placeHolderText}`}
                    fullWidth
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                    className={`interaction-ask7g-input-textfield ${extraClass}`}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton edge="end" color="primary" onClick={handleAsk} sx={{ p: 2 }}>
                            <img
                              src={Ask7gArrowIcon}
                              alt="send"
                              className={`ask7g-send-icon ${input.length > 0 ? "active" : ""}`}
                            />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default InteractionAsk7G;
